import { getLocale } from '../i18n/locale'
import {
  Answer,
  ScenarioType,
  CurrentProviderCookieNameType,
  ProviderType,
  Scenario,
  ProviderInfo,
} from '../state/interfaces'
import { getCookie, setCookie } from './storage'

export const removeNextOnEachAnswer = (answers: Array<Answer>) => {
  return answers.map(({ next: _, ...rest }) => ({ ...rest }))
}

export const getCurrentProviderCookieName = (scenarioType: ScenarioType | undefined) => {
  switch (scenarioType) {
    case ScenarioType.MOBILE:
      return CurrentProviderCookieNameType.CURRENT_MOBILE_PROVIDER
    case ScenarioType.INTERNET:
      return CurrentProviderCookieNameType.CURRENT_INTERNET_PROVIDER
    default:
      return ''
  }
}

export const getProviderTypeFromScenario = (scenario: Scenario | null): ProviderType => {
  if (
    scenario?.type === ScenarioType.INTERNET ||
    scenario?.type === ScenarioType.INTERNET_REVIEWS ||
    scenario?.type === ScenarioType.INTERNET_ALERT ||
    scenario?.type === ScenarioType.INTERNET_REVIEWS_AWARD
  ) {
    return ProviderType.INTERNET
  }

  return ProviderType.MOBILE
}

export const mapProvince = (shortName: string) => {
  const map: Record<string, string> = {
    qc: 'quebec',
    on: 'ontario',
    sk: 'saskatchewan',
    ab: 'alberta',
    mb: 'manitoba',
    nb: 'new-brunswick',
    ns: 'nova-scotia',
    pe: 'prince-edward',
    nl: 'newfoundland-labrador',
    yk: 'yukon',
    bc: 'british-columbia',
    nt: 'northwest-territories',
    nu: 'nunavut',
  }

  if (map[shortName]) {
    return map[shortName]
  }

  return shortName
}

type provinceTranslation = {
  en: string
  fr: string
}

export const mapProvinceFromSlug = (slug: string) => {
  const locale = getLocale()
  const map: Record<string, provinceTranslation> = {
    quebec: {
      en: 'Quebec',
      fr: 'Québec',
    },
    ontario: {
      en: 'Ontario',
      fr: 'Ontario',
    },
    saskatchewan: {
      en: 'Saskatchewan',
      fr: 'Saskatchewan',
    },
    alberta: {
      en: 'Alberta',
      fr: 'Alberta',
    },
    manitoba: {
      en: 'Manitoba',
      fr: 'Manitoba',
    },
    'new-brunswick': {
      en: 'New Brunswick',
      fr: 'Nouveau-Brunswick',
    },
    'nova-scotia': {
      en: 'Nova scotia',
      fr: 'Nouvelle-Écosse',
    },
    'prince-edward': {
      en: 'Price Edward Island',
      fr: 'Île-du-Prince-Édouard',
    },
    'newfoundland-labrador': {
      en: 'Newfoundland and Labrador',
      fr: 'Terre-Neuve-et-Labrador',
    },
    yukon: {
      en: 'Yukon',
      fr: 'Yukon',
    },
    'british-columbia': {
      en: 'British Columbia',
      fr: 'Colombie britannique',
    },
    'northwest-territories': {
      en: 'Northwest Territories',
      fr: 'Territoires du Nord-Ouest',
    },
    nunavut: {
      en: 'Nunavut',
      fr: 'Nunavut',
    },
  }

  if (map[slug]) {
    return map[slug][locale]
  }

  return slug
}

export const formatPostalCode = (postalCode: string) => {
  return postalCode.toUpperCase()
}

export const preparePostalCodeForUrl = (postalCode: string) => {
  return postalCode.toLowerCase().replace(/\s+/g, '')
}

export const findProviderNameWithSlug = (providerSlug: string, scenario: Scenario) => {
  let endpoint = `${process.env.REACT_APP_MOBILE_PROVIDERS_API_URL}`
  const providerType: ProviderType = getProviderTypeFromScenario(scenario)

  if (providerType === ProviderType.INTERNET) {
    endpoint = `${process.env.REACT_APP_INTERNET_PROVIDERS_API_URL}`
  }

  fetch(endpoint)
    .then((result) => result.json())
    .then((providers) => setCookiesProviderUsingSlug(providerSlug, providers))
}

export const findProviderSlugWithName = (providerName: string, scenario: Scenario) => {
  let endpoint = `${process.env.REACT_APP_MOBILE_PROVIDERS_API_URL}`
  const providerType: ProviderType = getProviderTypeFromScenario(scenario)

  if (providerType === ProviderType.INTERNET) {
    endpoint = `${process.env.REACT_APP_INTERNET_PROVIDERS_API_URL}`
  }

  fetch(endpoint)
    .then((result) => result.json())
    .then((providers) => setCookiesProviderUsingName(providerName, providers))
}

export const setCookiesProviderUsingSlug = (providerSlug: string, providers: ProviderInfo[]) => {
  const provider = providers.find((provider) => provider.slugEn === providerSlug)
  if (provider) {
    setCookie('providerNameReviews', provider.nameEn)
    setCookie('providerSlugReviews', providerSlug)
  }
}

export const setCookiesProviderUsingName = (providerName: string, providers: ProviderInfo[]) => {
  const provider = providers.find((provider) => provider.nameEn === providerName)
  if (provider) {
    setCookie('providerNameReviews', providerName)
    setCookie('providerSlugReviews', provider.slugEn)
  }
}

export type provinceObjectType = {
  name: {
    en: string
    fr: string
  }
  prepositions: {
    en: string
    fr: string
  }
}

export const createProvinceObject = (provinceslug: string) => {
  const map: Record<string, provinceObjectType> = {
    quebec: {
      name: {
        en: 'Quebec',
        fr: 'Québec',
      },
      prepositions: {
        en: '',
        fr: 'au ',
      },
    },
    ontario: {
      name: {
        en: 'Ontario',
        fr: 'Ontario',
      },
      prepositions: {
        en: '',
        fr: 'en ',
      },
    },
    saskatchewan: {
      name: {
        en: 'Saskatchewan',
        fr: 'Saskatchewan',
      },
      prepositions: {
        en: '',
        fr: 'en',
      },
    },
    alberta: {
      name: {
        en: 'Alberta',
        fr: 'Alberta',
      },
      prepositions: {
        en: '',
        fr: 'en ',
      },
    },
    manitoba: {
      name: {
        en: 'Manitoba',
        fr: 'Manitoba',
      },
      prepositions: {
        en: '',
        fr: 'au ',
      },
    },
    'new-brunswick': {
      name: {
        en: 'New Brunswick',
        fr: 'Nouveau-Brunswick',
      },
      prepositions: {
        en: '',
        fr: 'au ',
      },
    },
    'nova-scotia': {
      name: {
        en: 'Nova scotia',
        fr: 'Nouvelle Écosse',
      },
      prepositions: {
        en: '',
        fr: 'en ',
      },
    },
    'prince-edward': {
      name: {
        en: 'Prince Edward Island',
        fr: 'Ile-du-Prince-Édouard',
      },
      prepositions: {
        en: '',
        fr: "à l'",
      },
    },
    'newfoundland-labrador': {
      name: {
        en: 'Newfoundland and Labrador',
        fr: 'Terre-Neuve-et-Labrador',
      },
      prepositions: {
        en: '',
        fr: 'à ',
      },
    },
    yukon: {
      name: {
        en: 'Yukon',
        fr: 'Yukon',
      },
      prepositions: {
        en: '',
        fr: 'au ',
      },
    },
    'british-columbia': {
      name: {
        en: 'British Columbia',
        fr: 'Colombie-Britanique',
      },
      prepositions: {
        en: '',
        fr: 'en ',
      },
    },
    'northwest-territories': {
      name: {
        en: 'Northwest Territories',
        fr: 'Territoires du Nord-Ouest',
      },
      prepositions: {
        en: '',
        fr: 'aux ',
      },
    },
    nunavut: {
      name: {
        en: 'Nunavut',
        fr: 'Nunavut',
      },
      prepositions: {
        en: '',
        fr: 'au ',
      },
    },
  }

  if (map[provinceslug]) {
    return map[provinceslug]
  }

  return null
}

export const updateCookieProvince = (postalCode: string) => {
  const province = findProvinceSlugWithFirstLetterOfPostalCode(postalCode)
  setCookie('province', province)
}

export const findProvinceSlugWithFirstLetterOfPostalCode = (postalCode: string) => {
  const currentProvince = getCookie('province')
  const firstLetter = postalCode.charAt(0)

  // voir corresponding letter in switch below
  if (firstLetter.toLocaleLowerCase() === 'x' && currentProvince === 'northwest-territories') {
    return currentProvince
  }

  switch (firstLetter) {
    case 'A':
      return 'newfoundland-labrador'
    case 'B':
      return 'nova-scotia'
    case 'C':
      return 'prince-edward'
    case 'E':
      return 'new-brunswick'
    case 'G':
    case 'H':
    case 'J':
      return 'quebec'
    case 'K':
    case 'L':
    case 'M':
    case 'N':
    case 'P':
      return 'ontario'
    case 'R':
      return 'manitoba'
    case 'S':
      return 'saskatchewan'
    case 'T':
      return 'alberta'
    case 'V':
      return 'british-columbia'
    case 'X':
      return 'nunavut' // also 'northwest-territories'
    case 'Y':
      return 'yukon'
    default:
      return 'quebec' // default to quebec
  }
}

// These keys are defined here https://sheet.zoho.com/sheet/open/3xatu4b719a2e458441709c1fbdf424ab2f58?sheet=Feuille1&range=C:C
// Changes to the keys should be updated in Portal: \App\Service\Analytics\GoogleAnalytics
// Changes to the keys should be updated in Gocontroller: src/Celagora/CoreBundle/Controller/GoController.php
// Changes to the keys should be updated in js used by grunt: src/Celagora/CoreBundle/Resources/js/lib/functions/events/events.js
// Changes to the keys should be updated in assets: assets/js/common/functions/functions.js
export function mapKeySearchCriterias() {
  return {
    KEY_SEARCH_DATA_AMOUNT: 'a',
    KEY_SEARCH_MONTHLY_USAGE: 'b',
    KEY_SEARCH_OTHER_FILTERS: 'c',
    KEY_SEARCH_DOWNLOAD_SPEED: 'd',
    KEY_SEARCH_POSTAL_CODE: 'e',
    KEY_SEARCH_PHONE_MODEL: 'f',
    KEY_SEARCH_VOICE: 'g',
    KEY_RESULT_PROVIDER_SLUG: 'h',
    KEY_RESULT_PLAN_MONTHLY_USAGE: 'i',
    KEY_RESULT_TOTAL_MONTHLY_PRICE: 'j',
    KEY_RESULT_COMBINATION_ID: 'k',
    KEY_RESULT_PLAN_CPC: 'l',
    KEY_RESULT_PLAN_DOWNLOAD_SPEED: 'm',
    KEY_RESULT_PLAN_TECHNOLOGY: 'n',
    KEY_RESULT_TOTAL_UPFRONT_PRICE: 'o',
    KEY_RESULT_PLAN_DATA_AMOUNT: 'p',
    KEY_RESULT_PLAN_VOICE: 'q',
    KEY_RESULT_TOTAL_2YEARS_PRICE: 'r',
    KEY_RESULT_PLAN_MONTHLY_PRICE: 's',
  }
}

export function mapZipAdditionalInfoLead() {
  return {
    KEY_CATEGORY_NAME: 'a',
    KEY_CSV: 'b',
    KEY_DOWNLOAD_SPEED: 'c',
    KEY_LINE_ITEM_ID: 'd',
    KEY_NAV: 'e',
    KEY_NETWORK: 'f',
    KEY_PLAN_DATA_USAGE: 'g',
    KEY_PLAN_ID: 'h',
    KEY_PLAN_MONTHLY_PRICE: 'i',
    KEY_PLAN_NAME: 'j',
    KEY_PLAN_UPFRONT_PRICE: 'k',
    KEY_PROVIDER: 'l',
    KEY_PROVIDER_NAME: 'm',
    KEY_PROVIDER_SLUG: 'n',
    KEY_SC_OPTIONS: 'o',
    KEY_SC_UPLOAD_SPEED: 'p',
    KEY_SC_URL: 'q',
    KEY_SR_BANDWIDTH: 'r',
    KEY_SR_DOWNLOAD_SPEED: 's',
    KEY_SR_INSTALL_FEES: 't',
    KEY_SR_LINK: 'u',
    KEY_SR_PLAN_NAME_EN: 'v',
    KEY_SR_PLAN_NAME_FR: 'w',
    KEY_SR_TECHNOLOGY: 'x',
    KEY_SR_TOTAL_1YEAR_PRICE: 'y',
    KEY_SR_TOTAL_2YEARS_PRICE: 'z',
    KEY_SR_TOTAL_MONTHLY_PRICE: 'aa',
    KEY_SR_TOTAL_UPFRONT_PRICE: 'ab',
    KEY_SR_UPLOAD_SPEED: 'ac',
    KEY_TL: 'ad',
    KEY_SC_DATA: 'ae',
    KEY_SC_VOICE: 'af',
    KEY_SC_PHONE: 'ag',
    KEY_SR_DATA: 'ah',
    KEY_SR_VOICE: 'ai',
    KEY_SR_TEXT: 'aj',
    KEY_SR_PHONE_MODEL: 'ak',
    KEY_CAMPAIGN_ID: 'al',
    KEY_BUNDLE_STATUS: 'am',
  }
}

export function mapAdditionalInfoLead() {
  return {
    categoryName: 'KEY_CATEGORY_NAME',
    csv: 'KEY_CSV',
    downloadSpeed: 'KEY_DOWNLOAD_SPEED',
    lineItemId: 'KEY_LINE_ITEM_ID',
    campaignId: 'KEY_CAMPAIGN_ID',
    nav: 'KEY_NAV',
    network: 'KEY_NETWORK',
    planDataUsage: 'KEY_PLAN_DATA_USAGE',
    planId: 'KEY_PLAN_ID',
    planMonthlyPrice: 'KEY_PLAN_MONTHLY_PRICE',
    planName: 'KEY_PLAN_NAME',
    planUpfrontPrice: 'KEY_PLAN_UPFRONT_PRICE',
    provider: 'KEY_PROVIDER',
    providerName: 'KEY_PROVIDER_NAME',
    providerSlug: 'KEY_PROVIDER_SLUG',
    sc_options: 'KEY_SC_OPTIONS',
    sc_uploadSpeed: 'KEY_SC_UPLOAD_SPEED',
    sc_url: 'KEY_SC_URL',
    sr_bandwidth: 'KEY_SR_BANDWIDTH',
    sr_downloadSpeed: 'KEY_SR_DOWNLOAD_SPEED',
    sr_installFees: 'KEY_SR_INSTALL_FEES',
    sr_link: 'KEY_SR_LINK',
    sr_planNameEn: 'KEY_SR_PLAN_NAME_EN',
    sr_planNameFr: 'KEY_SR_PLAN_NAME_FR',
    sr_technology: 'KEY_SR_TECHNOLOGY',
    sr_total1YearPrice: 'KEY_SR_TOTAL_1YEAR_PRICE',
    sr_total2YearsPrice: 'KEY_SR_TOTAL_2YEARS_PRICE',
    sr_totalMonthlyPrice: 'KEY_SR_TOTAL_MONTHLY_PRICE',
    sr_totalUpfrontPrice: 'KEY_SR_TOTAL_UPFRONT_PRICE',
    sr_uploadSpeed: 'KEY_SR_UPLOAD_SPEED',
    tl: 'KEY_TL',
    sc_data: 'KEY_SC_DATA',
    sc_voice: 'KEY_SC_VOICE',
    sc_phone: 'KEY_SC_PHONE',
    sr_data: 'KEY_SR_DATA',
    sr_voice: 'KEY_SR_VOICE',
    sr_text: 'KEY_SR_TEXT',
    sr_phoneModel: 'KEY_SR_PHONE_MODEL',
    bundle_status: 'KEY_BUNDLE_STATUS',
    sr_province: 'KEY_SR_PROVINCE',
  }
}

export function mapUnzipAdditionalInfoLead() {
  return {
    a: 'categoryName',
    b: 'csv',
    c: 'downloadSpeed',
    d: 'lineItemId',
    e: 'nav',
    f: 'network',
    g: 'planDataUsage',
    h: 'planId',
    i: 'planMonthlyPrice',
    j: 'planName',
    k: 'planUpfrontPrice',
    l: 'provider',
    m: 'providerName',
    n: 'providerSlug',
    o: 'sc_options',
    p: 'sc_uploadSpeed',
    q: 'sc_url',
    r: 'sr_bandwidth',
    s: 'sr_downloadSpeed',
    t: 'sr_installFees',
    u: 'sr_link',
    v: 'sr_planNameEn',
    w: 'sr_planNameFr',
    x: 'sr_technology',
    y: 'sr_total1YearPrice',
    z: 'sr_total2YearsPrice',
    aa: 'sr_totalMonthlyPrice',
    ab: 'sr_totalUpfrontPrice',
    ac: 'sr_uploadSpeed',
    ad: 'tl',
    ae: 'sc_data',
    af: 'sc_voice',
    ag: 'sc_phone',
    ah: 'sr_data',
    ai: 'sr_voice',
    aj: 'sr_text',
    ak: 'sr_phoneModel',
    al: 'campaignId',
    am: 'bundle_status',
    an: 'sr_province',
  }
}

export const addressLead = {
  address: '',
  status: 'not sent',
}
